import React, { useState, useEffect } from 'react';
import { supabase } from '../../../config/supabase';
import './CustomerAnalytics.css';

const CustomerAnalytics = () => {
  const [customerBreakdown, setCustomerBreakdown] = useState({
    new: { count: 0, percentage: 0, growth: 0 },
    returning: { count: 0, percentage: 0 },
    inactive: { count: 0, percentage: 0 }
  });
  const [retentionMetrics, setRetentionMetrics] = useState({
    retentionRate: 0,
    repeatOrderRate: 0,
    avgOrdersPerCustomer: 0
  });
  const [orderFrequency, setOrderFrequency] = useState([
    { range: '1-2 orders', count: 0 },
    { range: '3-5 orders', count: 0 },
    { range: '6-10 orders', count: 0 },
    { range: '11+ orders', count: 0 }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerAnalytics();
  }, []);

  const fetchCustomerAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch all customers
      const { data: customers } = await supabase
        .from('customers')
        .select('id, created_at');

      // Fetch all orders
      const { data: orders } = await supabase
        .from('orders')
        .select('customer_id, created_at');

      // Calculate customer breakdown
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const newCustomers = (customers || []).filter(c => new Date(c.created_at) >= thirtyDaysAgo).length;
      const customersWithOrders = new Set((orders || []).map(o => o.customer_id).filter(id => id !== null));
      const returningCustomers = customersWithOrders.size;
      const inactiveCustomers = (customers || []).length - returningCustomers;

      const totalCustomers = (customers || []).length;
      const newPercentage = totalCustomers > 0 ? Math.round((newCustomers / totalCustomers) * 100) : 0;
      const returningPercentage = totalCustomers > 0 ? Math.round((returningCustomers / totalCustomers) * 100) : 0;
      const inactivePercentage = totalCustomers > 0 ? Math.round((inactiveCustomers / totalCustomers) * 100) : 0;

      // Calculate previous period new customers for growth
      const previousStartDate = new Date(thirtyDaysAgo);
      previousStartDate.setDate(previousStartDate.getDate() - 30);
      const { count: previousNewCustomers } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', previousStartDate.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const growth = previousNewCustomers > 0 
        ? Math.round(((newCustomers - previousNewCustomers) / previousNewCustomers) * 100)
        : 0;

      setCustomerBreakdown({
        new: { count: newCustomers, percentage: newPercentage, growth },
        returning: { count: returningCustomers, percentage: returningPercentage },
        inactive: { count: inactiveCustomers, percentage: inactivePercentage }
      });

      // Calculate retention metrics
      const customersWithMultipleOrders = new Set();
      const orderCountsByCustomer = {};
      (orders || []).forEach(order => {
        if (order.customer_id) {
          if (!orderCountsByCustomer[order.customer_id]) {
            orderCountsByCustomer[order.customer_id] = 0;
          }
          orderCountsByCustomer[order.customer_id]++;
          if (orderCountsByCustomer[order.customer_id] > 1) {
            customersWithMultipleOrders.add(order.customer_id);
          }
        }
      });

      const retentionRate = totalCustomers > 0 
        ? Math.round((customersWithMultipleOrders.size / totalCustomers) * 100)
        : 0;
      const repeatOrderRate = customersWithOrders.size > 0
        ? Math.round((customersWithMultipleOrders.size / customersWithOrders.size) * 100)
        : 0;
      const avgOrdersPerCustomer = totalCustomers > 0
        ? ((orders || []).length / totalCustomers).toFixed(1)
        : 0;

      setRetentionMetrics({
        retentionRate,
        repeatOrderRate,
        avgOrdersPerCustomer: parseFloat(avgOrdersPerCustomer)
      });

      // Calculate order frequency
      const frequencyCounts = { '1-2': 0, '3-5': 0, '6-10': 0, '11+': 0 };
      Object.values(orderCountsByCustomer).forEach(count => {
        if (count <= 2) frequencyCounts['1-2']++;
        else if (count <= 5) frequencyCounts['3-5']++;
        else if (count <= 10) frequencyCounts['6-10']++;
        else frequencyCounts['11+']++;
      });

      setOrderFrequency([
        { range: '1-2 orders', count: frequencyCounts['1-2'] },
        { range: '3-5 orders', count: frequencyCounts['3-5'] },
        { range: '6-10 orders', count: frequencyCounts['6-10'] },
        { range: '11+ orders', count: frequencyCounts['11+'] }
      ]);
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const maxFrequency = Math.max(...orderFrequency.map(f => f.count), 1);

  return (
    <div className="customer-analytics-card">
      <h2 className="section-title">Customer Insights</h2>
      
      <div className="customer-analytics-grid">
        {/* Customer Breakdown */}
        <div className="analytics-column">
          <h3 className="column-title">Customer Breakdown</h3>
          
          <div className="breakdown-item new">
            <div className="breakdown-header">
              <span className="breakdown-label">New Customers</span>
              <span className="breakdown-percentage">{customerBreakdown.new.percentage}%</span>
            </div>
            <div className="breakdown-number">{customerBreakdown.new.count}</div>
            <div className={`breakdown-growth ${customerBreakdown.new.growth >= 0 ? 'positive' : 'negative'}`}>
              <span>{customerBreakdown.new.growth >= 0 ? '↑' : '↓'}</span> {customerBreakdown.new.growth >= 0 ? '+' : ''}{customerBreakdown.new.growth}% vs previous
            </div>
          </div>

          <div className="breakdown-item returning">
            <div className="breakdown-header">
              <span className="breakdown-label">Returning Customers</span>
              <span className="breakdown-percentage">{customerBreakdown.returning.percentage}%</span>
            </div>
            <div className="breakdown-number">{customerBreakdown.returning.count}</div>
          </div>

          <div className="breakdown-item inactive">
            <div className="breakdown-header">
              <span className="breakdown-label">Inactive (30+ days)</span>
              <span className="breakdown-percentage">{customerBreakdown.inactive.percentage}%</span>
            </div>
            <div className="breakdown-number">{customerBreakdown.inactive.count}</div>
          </div>

          <div className="stacked-bar-chart">
            <div className="stacked-bar">
              <div
                className="stacked-segment new-segment"
                style={{ width: `${customerBreakdown.new.percentage}%` }}
              />
              <div
                className="stacked-segment returning-segment"
                style={{ width: `${customerBreakdown.returning.percentage}%` }}
              />
              <div
                className="stacked-segment inactive-segment"
                style={{ width: `${customerBreakdown.inactive.percentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Retention Metrics */}
        <div className="analytics-column">
          <h3 className="column-title">Retention Metrics</h3>
          
          <div className="retention-metric">
            <div className="metric-ring">
              <svg viewBox="0 0 120 120" className="ring-svg">
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#E9ECEF"
                  strokeWidth="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#fe0000"
                  strokeWidth="8"
                  strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - retentionMetrics.retentionRate / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="ring-value">{retentionMetrics.retentionRate}%</div>
            </div>
            <div className="metric-label">Retention Rate</div>
          </div>

          <div className="retention-metric">
            <div className="metric-ring">
              <svg viewBox="0 0 100 100" className="ring-svg">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#E9ECEF"
                  strokeWidth="6"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#2D6A4F"
                  strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - retentionMetrics.repeatOrderRate / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="ring-value-small">{retentionMetrics.repeatOrderRate}%</div>
            </div>
            <div className="metric-label">Repeat Order Rate</div>
          </div>

          <div className="retention-metric">
            <div className="metric-number-large">{retentionMetrics.avgOrdersPerCustomer}</div>
            <div className="metric-label">Average Orders per Customer</div>
          </div>
        </div>

        {/* Order Frequency */}
        <div className="analytics-column">
          <h3 className="column-title">Order Frequency</h3>
          
          <div className="frequency-list">
            {orderFrequency.map((freq, i) => (
              <div key={i} className="frequency-item">
                <div className="frequency-label">{freq.range}</div>
                <div className="frequency-bar-wrapper">
                  <div
                    className="frequency-bar"
                    style={{ width: `${(freq.count / maxFrequency) * 100}%` }}
                  />
                </div>
                <div className="frequency-count">{freq.count} customers</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalytics;

